/**
 * **Nudge** — the one action the Tasks Assigned tab has that no board surface offers
 * (`docs/13-tasks-section-proposal.md` §2, `specs/M10-tasks.md` §7).
 *
 * It is not a notification of its own: `tasks.nudge` posts an **update** on the item mentioning
 * the assignee, through the updates service, and the `mention` notification, the Updates tab,
 * the feed and the e-mail all follow from that one write. The api refuses it unless the caller
 * actually delegated that task, so this dialog never has to decide who may nudge whom.
 *
 * The wording is pre-filled and editable, because a nudge with a reason is a different message
 * from a nudge without one and only the person sending it knows which this is.
 */
import { useEffect, useRef, useState } from 'react';
import { useOverlayDismiss } from '@elizmonday/ui';
import { defaultNudgeMessage } from './vocabulary';
import type { TaskRowView } from './types';

export interface NudgeDialogProps {
  row: TaskRowView;
  onSend(message: string): Promise<void> | void;
  onClose(): void;
}

export const NUDGE_MAX_LENGTH = 2000;

export function NudgeDialog({ row, onSend, onClose }: NudgeDialogProps) {
  const [message, setMessage] = useState(() => defaultNudgeMessage(row.itemName));
  const [sending, setSending] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);

  useOverlayDismiss({ open: true, onClose });
  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);

  const send = () => {
    if (sending) return;
    setSending(true);
    void Promise.resolve(onSend(message.trim())).finally(() => setSending(false));
  };

  return (
    <div className="ez-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ez-tasks-nudge"
        role="dialog"
        aria-modal="true"
        aria-label={`Nudge ${row.assignee?.name ?? 'the assignee'}`}
        data-testid="tasks-nudge-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="ez-tasks-nudge__title">Nudge {row.assignee?.name ?? 'the assignee'}</h2>
        <p className="ez-tasks-nudge__hint">
          This posts an update on <strong>{row.itemName}</strong> and mentions them, exactly as
          writing it on the item would.
        </p>
        <textarea
          ref={field}
          className="ez-tasks-nudge__message"
          data-testid="tasks-nudge-message"
          rows={4}
          maxLength={NUDGE_MAX_LENGTH}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
        <div className="ez-tasks-nudge__actions">
          <button type="button" data-testid="tasks-nudge-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ez-tasks-nudge__send"
            data-testid="tasks-nudge-send"
            disabled={sending || message.trim() === ''}
            onClick={send}
          >
            {sending ? 'Sending…' : 'Send nudge'}
          </button>
        </div>
      </div>
    </div>
  );
}
